'use client'

import Skeleton, { SkeletonTheme } from 'react-loading-skeleton'
import 'react-loading-skeleton/dist/skeleton.css'

/**
 * Skeleton Loading Components
 * Provides consistent skeleton loading states across the app
 */

// Property Card Skeleton
export function PropertyCardSkeleton() {
  return (
    <SkeletonTheme baseColor="#f5f5f4" highlightColor="#e7e5e4">
      <div className="bg-white rounded-2xl shadow-sm border border-stone-200 overflow-hidden">
        {/* Image skeleton */}
        <Skeleton height={192} />
        
        <div className="p-6 space-y-4">
          {/* Title skeleton */}
          <Skeleton height={24} width="80%" />
          
          {/* Location skeleton */}
          <Skeleton height={16} width="60%" />
          
          {/* Price skeleton */}
          <Skeleton height={20} width="40%" />
          
          {/* Features skeleton */}
          <div className="flex gap-4">
            <Skeleton height={16} width={60} />
            <Skeleton height={16} width={60} />
            <Skeleton height={16} width={60} />
          </div>
          
          {/* Button skeleton */}
          <Skeleton height={40} borderRadius={8} />
        </div>
      </div>
    </SkeletonTheme>
  )
}

// Property List Skeleton
export function PropertyListSkeleton({ count = 3 }: { count?: number }) {
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
      {Array.from({ length: count }).map((_, index) => (
        <PropertyCardSkeleton key={index} />
      ))}
    </div>
  )
}

// Application Card Skeleton
export function ApplicationCardSkeleton() {
  return (
    <SkeletonTheme baseColor="#f5f5f4" highlightColor="#e7e5e4">
      <div className="bg-white rounded-lg shadow-sm border border-stone-200 p-6">
        <div className="space-y-4">
          {/* Header */}
          <div className="flex items-start justify-between">
            <div className="flex-1 space-y-2">
              <Skeleton height={20} width="70%" />
              <Skeleton height={16} width="50%" />
            </div>
            <Skeleton height={24} width={80} borderRadius={12} />
          </div>
          
          {/* Details */}
          <div className="space-y-2">
            <Skeleton height={16} width="100%" />
            <Skeleton height={16} width="80%" />
          </div>
          
          {/* Actions */}
          <div className="flex gap-3 pt-2">
            <Skeleton height={36} width={100} borderRadius={8} />
            <Skeleton height={36} width={100} borderRadius={8} />
          </div>
        </div>
      </div>
    </SkeletonTheme>
  )
}

// Application List Skeleton
export function ApplicationListSkeleton({ count = 5 }: { count?: number }) {
  return (
    <div className="space-y-4">
      {Array.from({ length: count }).map((_, index) => (
        <ApplicationCardSkeleton key={index} />
      ))}
    </div>
  )
}

// Stats Card Skeleton
export function StatCardSkeleton() {
  return (
    <SkeletonTheme baseColor="#f5f5f4" highlightColor="#e7e5e4">
      <div className="bg-white rounded-2xl shadow-sm border border-stone-200 p-6">
        <div className="space-y-3">
          <Skeleton height={48} width={48} borderRadius={12} />
          <Skeleton height={16} width="60%" />
          <Skeleton height={32} width="40%" />
          <Skeleton height={14} width="80%" />
        </div>
      </div>
    </SkeletonTheme>
  )
}

// Stats Grid Skeleton
export function StatsGridSkeleton({ count = 3 }: { count?: number }) {
  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
      {Array.from({ length: count }).map((_, index) => (
        <StatCardSkeleton key={index} />
      ))}
    </div>
  )
}

// Table Row Skeleton
export function TableRowSkeleton({ columns = 5 }: { columns?: number }) {
  return (
    <SkeletonTheme baseColor="#f5f5f4" highlightColor="#e7e5e4">
      <tr>
        {Array.from({ length: columns }).map((_, index) => (
          <td key={index} className="px-6 py-4">
            <Skeleton height={20} />
          </td>
        ))}
      </tr>
    </SkeletonTheme>
  )
}

// Table Skeleton
export function TableSkeleton({ rows = 5, columns = 5 }: { rows?: number; columns?: number }) {
  return (
    <SkeletonTheme baseColor="#f5f5f4" highlightColor="#e7e5e4">
      <div className="bg-white rounded-lg shadow-sm border border-stone-200 overflow-hidden">
        <table className="min-w-full divide-y divide-stone-200">
          <thead className="bg-stone-50">
            <tr>
              {Array.from({ length: columns }).map((_, index) => (
                <th key={index} className="px-6 py-3">
                  <Skeleton height={16} />
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-stone-200">
            {Array.from({ length: rows }).map((_, rowIndex) => (
              <TableRowSkeleton key={rowIndex} columns={columns} />
            ))}
          </tbody>
        </table>
      </div>
    </SkeletonTheme>
  )
}

// Chat Message Skeleton
export function ChatMessageSkeleton({ isOwn = false }: { isOwn?: boolean }) {
  return (
    <SkeletonTheme baseColor="#f5f5f4" highlightColor="#e7e5e4">
      <div className={`flex ${isOwn ? 'justify-end' : 'justify-start'} mb-4`}>
        <div className={`max-w-xs lg:max-w-md ${isOwn ? 'order-2' : 'order-1'}`}>
          {!isOwn && <Skeleton height={16} width={80} className="mb-1" />}
          <Skeleton height={60} width={200} borderRadius={12} />
          <Skeleton height={12} width={60} className="mt-1" />
        </div>
        {!isOwn && (
          <div className="order-2 ml-2">
            <Skeleton circle height={32} width={32} />
          </div>
        )}
      </div>
    </SkeletonTheme>
  )
}

// Chat Skeleton
export function ChatSkeleton({ messageCount = 3 }: { messageCount?: number }) {
  return (
    <div className="space-y-4 p-4">
      {Array.from({ length: messageCount }).map((_, index) => (
        <ChatMessageSkeleton key={index} isOwn={index % 2 === 0} />
      ))}
    </div>
  )
}

